import { NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoginByUserPasswordComponent } from '../../components/login-by-user-password/login-by-user-password.component';
import { OrganizationType } from '../../types/app.step.type';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { AuthService, HttpErrorResponse } from '../../services/auth.service';
import { LoadingComponent } from '../../components/loading/loading.component';
import { PAGE_ROUTES, SESSION_KEY_NAME } from '../../constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { isEmail } from '../../utils/email.util';
import { catchError, finalize, map, Observable, throwError } from 'rxjs';
import { showSnackBar } from '../../utils/snackBar.ulti';
import { AuthkitService } from '../../services/authKit.service';
import { extractErrors } from '../../utils/extracErrors.util';

type CheckUsernameOrEmailMapType = {
  isExistedUser: boolean;
  isMigratedUser: boolean;
  username: string;
  redirectUri: string;
};

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [NgIf, LoginByUserPasswordComponent, LoadingComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit {
  protected username?: string | null;
  protected isLoading = false;

  private readonly _migrateAccountReminder = 'This account hasn’t been migrated. Continue to begin the migration process.';
  private readonly _magicAuthCodeErrorMessage = 'The one-time code was previously used or is invalid';

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _authService: AuthService,
    private _snackBar: MatSnackBar,
    private _translate: TranslateService,
    private _authKitService: AuthkitService
  ) {}

  ngOnInit(): void {
    this._route.queryParamMap.subscribe(async (params) => {
      let email = decodeURIComponent(params.get('email') || '');
      email = email.replaceAll(' ', '+');
      const magicAuthCode = decodeURIComponent(params.get('magic-auth-code') || '');

      this.username = email;

      // init auth instance if it does not exist
      await this._authKitService.init({ redirectUri: window.location.origin });

      if (email && magicAuthCode) this._handlePrepareAuthWithMAC(email, magicAuthCode);
    });
  }

  private _handlePrepareAuthWithMAC(email: string, magicAuthCode: string): void {
    this.isLoading = true;

    // validate email
    if (email && !isEmail(email)) {
      this.isLoading = false;
      showSnackBar({ type: 'error', message: 'Email is invalid', snackBar: this._snackBar, translate: this._translate });

      return;
    }

    const checkUsernameOrEmail: Observable<CheckUsernameOrEmailMapType> = this._authService.checkUsernameOrEmail(email).pipe(
      map((result) => ({
        isExistedUser: result?.userExists,
        isMigratedUser: result?.isUserMigrated,
        username: result?.username,
        redirectUri: result?.appTypeId === 1 ? `${environment.adminRedirectUri}/#/login` : `${environment.defaultRedirectUri}/login`
      })),
      catchError((error: HttpErrorResponse) => {
        this._extractErrors(error);

        return throwError(() => error);
      })
    );

    this._handleAuthWithMAC<CheckUsernameOrEmailMapType>({ observable: checkUsernameOrEmail, email, magicAuthCode });
  }

  private _handleAuthWithMAC<T extends CheckUsernameOrEmailMapType>({
    observable,
    email,
    magicAuthCode
  }: {
    observable: Observable<T>;
    email: string;
    magicAuthCode: string;
  }): void {
    observable.subscribe((result) => {
      const { isExistedUser, isMigratedUser, redirectUri, username } = result;

      // Update username value
      this.username = username;

      if (!isExistedUser) {
        this._onUserNotExisted(email);

        return;
      }

      if (isMigratedUser) {
        this._onUserMigrated(email, magicAuthCode, redirectUri);

        return;
      }

      this._onUserNotMigrated(email);
    });
  }

  private _onUserNotExisted(email: string) {
    this._router.navigate([PAGE_ROUTES.NOT_FOUND], {
      state: { returnTo: window.location.pathname, queryParams: { email } }
    });
  }

  private _onUserNotMigrated(email: string) {
    showSnackBar({ type: 'error', message: this._migrateAccountReminder, snackBar: this._snackBar, translate: this._translate });

    // Signin with workos instance
    this._authKitService.signIn({ loginHint: email });
  }

  private _onUserMigrated(email: string, magicAuthCode: string, redirectUri: string) {
    // Autologin with magic-code
    this._authService
      .loginWithMagicCode({ email, code: magicAuthCode })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (result) => {
          const accessToken = result.access_token;
          const pendingToken = result.pending_authentication_token;
          const organizations = result.organizations as OrganizationType[];
          const sessionId = result.session_id;

          if (accessToken && sessionId) {
            // Store the session email and session_id for authenticate success withmagic-auth-code
            localStorage.setItem(SESSION_KEY_NAME.WORKOS.previous_session_login_hint, result.login_hint as string);
            localStorage.setItem(SESSION_KEY_NAME.WORKOS.session_id, result.session_id as string);

            const queryParams = { accessToken, redirectUri, sessionId };

            // Redirect to susscess page
            this._router.navigate([PAGE_ROUTES.SUCCESS], { queryParams });
          } else if (pendingToken && organizations) {
            const queryParams = { pendingToken, organizations: JSON.stringify(organizations), redirectUri };

            // Redirect to select organizations page
            this._router.navigate([PAGE_ROUTES.ORGANIZATIONS], { queryParams });
          }
        },
        error: () => {
          showSnackBar({
            type: 'error',
            message: this._magicAuthCodeErrorMessage,
            params: { email },
            snackBar: this._snackBar,
            translate: this._translate
          });
        }
      });
  }

  private _extractErrors(apiResponse: HttpErrorResponse): void {
    this.isLoading = false;
    const { status } = apiResponse ?? {};

    extractErrors({ status, snackBar: this._snackBar, translate: this._translate });
  }
}
